import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { SearchType } from "Dashboard/domain/SearchType";
import { useAppDispatch } from "hooks/hooks";
import {
  Control,
  Controller,
  UseFormGetValues,
  UseFormHandleSubmit,
  UseFormRegister,
  UseFormReset,
  UseFormSetFocus,
  UseFormSetValue
} from "react-hook-form";
import { getOutputs, setSearchPopover } from "../outputSlice";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLongArrowAltDown } from "@fortawesome/free-solid-svg-icons";
import classes from "../../../../../../components/Popover/popover.module.scss";
import classesCB from "Dashboard/scenes/ConcreteBase/styles/popover.module.scss";
import { IOutputSearch } from "Dashboard/scenes/ConcreteBase/domain/IOutputSearch";
import RepositorySelect from "../../Input/components/RepositorySelect";
import { styles } from "components/Popover/popoverSelectStyle";
import UnitSelect from "Dashboard/components/UnitSelect";
import { FormatDateIso } from "utils/dateUtils";
import DatePicker from "react-datepicker";

interface IProps {
  handleSubmit: UseFormHandleSubmit<IOutputSearch>;
  register: UseFormRegister<IOutputSearch>;
  control: Control<IOutputSearch>;
  reset: UseFormReset<IOutputSearch>;
  setFocus: UseFormSetFocus<IOutputSearch>;
  searchType: SearchType;
  setSearchType: React.Dispatch<React.SetStateAction<SearchType>>;
  getValues: UseFormGetValues<IOutputSearch>;
  setValue: UseFormSetValue<IOutputSearch>;
  setGetOutputListItemsSearchParams: Dispatch<SetStateAction<IOutputSearch>>;
};

const SearchPopover = (props: IProps) => {
  const dispatch = useAppDispatch();
  const { register, control, handleSubmit, getValues, setValue, setFocus, setGetOutputListItemsSearchParams } = props;

  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>(
    [getValues("dateFrom") ? new Date(getValues("dateFrom")!) : null,
    getValues("dateTo") ? new Date(getValues("dateTo")!) : null]);

  const onSubmit = async (data: IOutputSearch) => {
    if (props.searchType === SearchType.BASIC) {
      data = { searchQuery: data.searchQuery };
    } else {
      data.searchQuery = undefined;
    }
    setGetOutputListItemsSearchParams(data);
    dispatch(setSearchPopover({ isOpen: false }));
  };

  const switchSearchMode = (searchType: SearchType) => {
    props.setSearchType(searchType);
  };

  useEffect(() => {
    if (props.searchType === SearchType.BASIC) {
      setFocus("searchQuery");
    } else {
      setFocus("itemCode");
    }
  }, [setFocus, props.searchType]);

  return (
    <form
      className={`${classesCB.searchInputPopover} ${classes.basicPopover}`}
      onSubmit={handleSubmit(onSubmit)}
    >
      {props.searchType === SearchType.BASIC && (
        <>
          <div className={classes.row}>
            <div className={`${classes.col} ${classes.colDir}`}>
              <input
                type="text" className={`${classes.fullWidth} ${classes.itemBorder}`}
                {...register("searchQuery", { required: false })}
              />
            </div>
            <div className={classes.gPopoverDisclaimer}>
              * možete uneti više reči tj. kriterijuma pretrage
            </div>
          </div>

          <div className={classes.row}>
            <div className={classes.buttons}>
              <label
                onClick={() => switchSearchMode(SearchType.ADVANCED)}
                className={classes.clickableText}
              >
                NAPREDNA PRETRAGA
              </label>
              <button className={classes.iconButton} type="submit">
                <FontAwesomeIcon
                  icon={faLongArrowAltDown}
                  className={classes.icon} />
              </button>
            </div>
          </div>
        </>
      )}
      {props.searchType === SearchType.ADVANCED && (
        <>
          <div className={classes.row}>
            <div className={`${classes.col} ${classes.rowDir}`}>
              <label className={classes.mr5}>šifra artikla</label>
              <input type="text" className={classes.itemBorder}
                {...register("itemCode", { required: false })} />
            </div>
          </div>

          <div className={classes.row}>
            <div className={`${classes.col} ${classes.rowDir}`}>
              <label className={classes.mr5}>br. otpremnice</label>
              <input type="text" className={classes.itemBorder}
                {...register("billOfLandingIdRange", { required: false })} />
            </div>
          </div>

          <div className={classes.row}>
            <div className={`${classes.col} ${classes.rowDir}`}>
              <label className={classes.mr5}>br. trebovanja</label>
              <input type="text" className={classes.itemBorder}
                {...register("requisitionIdRange", { required: false })} />
            </div>
          </div>

          <div className={classes.row}>
            <div className={`${classes.col} ${classes.rowDir}`}>
              <label className={classes.mr5}>naziv</label>
              <input type="text" className={classes.itemBorder}
                {...register("name", { required: false })} />
            </div>
          </div>

          <div className={classes.row}>
            <div className={`${classes.col} ${classes.rowDir}`}>
              <label className={classes.mr5}>magacin</label>

              <Controller
                name="repositoryId"
                control={control}
                defaultValue={undefined}
                rules={{
                  required: false,
                }}
                render={({ field: { onChange } }) => (
                  <RepositorySelect
                    value={getValues("repositoryId")}
                    onChange={onChange}
                    customStyles={styles.customStyleClassic}
                  />
                )}
              />
            </div>
          </div>

          <div className={classes.row}>
            <div className={`${classes.col} ${classes.rowDir}`}>
              <label className={classes.mr5}>količina izdato</label>
              <input type="text" className={classes.itemBorder}
                {...register("quantityAmountRange", { required: false })} />
            </div>
          </div>

          <div className={classes.row}>
            <div className={`${classes.col} ${classes.rowDir}`}>
              <label className={classes.mr5}>mera</label>
              <Controller
                name="quantityUnit"
                control={control}
                defaultValue={undefined}
                rules={{
                  required: false,
                }}
                render={({ field: { onChange } }) => (
                  <UnitSelect
                    value={getValues("quantityUnit")}
                    onChange={onChange}
                    customStyles={styles.customStyleClassic}
                  />
                )}
              />
            </div>
          </div>

          <div className={classes.row}>
            <div className={`${classes.col} ${classes.rowDir}`}>
              <label className={classes.mr5}>datum izlaza</label>
              <div className="datepickerWrapper">
                <DatePicker
                  selectsRange={true}
                  startDate={dateRange[0]}
                  endDate={dateRange[1]}
                  showMonthDropdown
                  showYearDropdown
                  onChange={(date: any) => {
                    setDateRange([date[0], date[1]]);
                    setValue("dateFrom", date[0] && FormatDateIso(date[0]));
                    setValue("dateTo", date[1] && FormatDateIso(date[1]));
                  }}
                  isClearable={true}
                  className={`${classes.dateItem} ${classes.itemBorder}`}
                />
              </div>
            </div>
          </div>

          <div className={classes.row}>
            <div className={`${classes.col} ${classes.rowDir}`}>
              <label className={classes.mr5}>odvezao</label>
              <input type="text" className={classes.itemBorder}
                {...register("takenAwayByDriver", { required: false })} />
            </div>
          </div>

          <div className={classes.row}>
            <div className={`${classes.col} ${classes.rowDir}`}>
              <label className={classes.mr5}>registracija</label>
              <input type="text" className={classes.itemBorder}
                {...register("takenAwayByVehicle", { required: false })} />
            </div>
          </div>

          <div className={classes.row}>
            <div className={`${classes.col} ${classes.rowDir}`}>
              <label className={classes.mr5}>gde je otišlo</label>
              <input type="text" className={classes.itemBorder}
                {...register("outputFor", { required: false })} />
            </div>
          </div>

          <div className={classes.row}>
            <div className={`${classes.col} ${classes.rowDir}`}>
              <label className={classes.mr5}>dobavljač</label>
              <input type="text" className={classes.itemBorder}
                {...register("supplier", { required: false })} />
            </div>
          </div>

          <div className={classes.row}>
            <div className={`${classes.col} ${classes.rowDir}`}>
              <label className={classes.mr5}>napomena</label>
              <input type="text" className={classes.itemBorder}
                {...register("note", { required: false })} />
            </div>
          </div>

          <div className={classes.row}>
            <div className={classes.buttons}>
              <label
                onClick={() => switchSearchMode(SearchType.BASIC)}
                className={classes.clickableText}
              >
                PAMETNA PRETRAGA
              </label>
              <button className={classes.iconButton} type="submit">
                <FontAwesomeIcon
                  icon={faLongArrowAltDown}
                  className={classes.icon} />
              </button>
            </div>
          </div>
        </>)}
    </form>
  );
};

export default SearchPopover;
